import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import PageSeo from "../components/PageSeo";
import bikesData from "../data/bikePrices.json";

const brandList = [
  "All",
  "Bajaj",
  "Hero",
  "Honda",
  "TVS",
  "Royal Enfield",
  "KTM",
  "Yamaha",
  "Suzuki",
  "Kawasaki",
  "Triumph",
  "Harley-Davidson",
  "Jawa",
];

const statCards = [
  { label: "Trending bikes", value: "25+" },
  { label: "Brands", value: "12" },
  { label: "Quick compare", value: "1 tap" },
];

const bikeAliasMap = {
  splendor: "splendor",
  "splendor-plus": "splendor",
  "hero-splendor-plus": "splendor",
  "hf-deluxe": "hf-deluxe",
  hfdeluxe: "hf-deluxe",
  "hero-hf-deluxe": "hf-deluxe",
};

const comparisonCategories = [
  {
    key: "price",
    label: "On-road price",
    better: "lower",
    getValue: (bike) => bike.price,
    format: (value) => `Rs ${formatNumber(value, 0)}`,
  },
  {
    key: "engine_cc",
    label: "Engine",
    better: "higher",
    getValue: (bike) => bike.engine_cc,
    format: (value) => `${formatNumber(value, 0)} cc`,
  },
  {
    key: "mileage",
    label: "Mileage",
    better: "higher",
    getValue: (bike) => bike.mileage,
    format: (value) => `${formatNumber(value, 0)} km/l`,
  },
  {
    key: "weight",
    label: "Weight",
    better: "lower",
    getValue: (bike) => bike.weight,
    format: (value) => `${formatNumber(value, 0)} kg`,
  },
  {
    key: "price_per_cc",
    label: "Price per cc",
    better: "lower",
    getValue: (bike) => bike.price / bike.engine_cc,
    format: (value) => `Rs ${formatNumber(value, 0)}/cc`,
  },
  {
    key: "power_weight",
    label: "Power-to-weight",
    better: "higher",
    getValue: (bike) => bike.engine_cc / bike.weight,
    format: (value) => `${value.toFixed(2)} cc/kg`,
  },
  {
    key: "efficiency_weight",
    label: "Mileage-to-weight",
    better: "higher",
    getValue: (bike) => bike.mileage / bike.weight,
    format: (value) => `${value.toFixed(2)} km/l/kg`,
  },
  {
    key: "best_for",
    label: "Best for",
    type: "text",
    getValue: (bike) => getBestFor(bike),
  },
];

function formatNumber(value, decimals = 1) {
  if (value == null || Number.isNaN(Number(value))) return "N/A";
  return Number(value).toLocaleString("en-IN", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function resolveBikeId(value) {
  const key = String(value || "").trim();
  return bikeAliasMap[key] || key;
}

function getBestFor(bike) {
  if (bike.mileage >= 60 && bike.weight <= 130) return "Daily commuting";
  if (bike.engine_cc >= 300) return "Highway touring";
  if (bike.type === "Adventure") return "Weekend exploring";
  if (bike.weight <= 150 && bike.mileage >= 45) return "City and office runs";
  return "Balanced riding";
}

function scoreCity(bike) {
  return bike.mileage * 1.4 + (180 - bike.weight) * 0.45;
}

function scoreValue(bike) {
  return bike.mileage * 1.2 + (200000 / bike.price) * 10;
}

function scoreHighway(bike) {
  return bike.engine_cc * 0.45 + bike.weight * 0.15;
}

export default function BikeCompare() {
  const navigate = useNavigate();
  const resultRef = useRef(null);
  const { slug } = useParams();
  const parsed = useMemo(() => {
    if (!slug) return { bike1: "", bike2: "" };
    const [bike1 = "", bike2 = ""] = slug.split("-vs-");
    return { bike1: resolveBikeId(bike1), bike2: resolveBikeId(bike2) };
  }, [slug]);

  const [brand, setBrand] = useState("All");
  const [search, setSearch] = useState("");
  const [bike1, setBike1] = useState(parsed.bike1);
  const [bike2, setBike2] = useState(parsed.bike2);
  const [showResult, setShowResult] = useState(Boolean(parsed.bike1 && parsed.bike2));
  const [toast, setToast] = useState("");

  useEffect(() => {
    setBike1(parsed.bike1);
    setBike2(parsed.bike2);
    setShowResult(Boolean(parsed.bike1 && parsed.bike2));
  }, [parsed.bike1, parsed.bike2]);

  const bikes = bikesData;

  const filteredBikes = useMemo(() => {
    const term = search.trim().toLowerCase();
    return bikes.filter((bike) => {
      const matchesBrand = brand === "All" || bike.brand === brand;
      const matchesSearch =
        !term ||
        bike.name.toLowerCase().includes(term) ||
        bike.brand.toLowerCase().includes(term) ||
        bike.type.toLowerCase().includes(term);
      return matchesBrand && matchesSearch;
    });
  }, [bikes, brand, search]);

  const bikeOne = bikes.find((bike) => bike.id === bike1);
  const bikeTwo = bikes.find((bike) => bike.id === bike2);

  useEffect(() => {
    if (bike1 && bike2 && bike1 !== bike2) {
      navigate(`/compare/${bike1}-vs-${bike2}`, { replace: true });
    }
  }, [bike1, bike2, navigate]);

  const compareReady = Boolean(bikeOne && bikeTwo && bikeOne.id !== bikeTwo.id);

  const compareWinner =
    compareReady &&
    comparisonCategories.reduce((score, category) => {
      if (category.type === "text") return score;
      const v1 = category.getValue(bikeOne);
      const v2 = category.getValue(bikeTwo);
      if (v1 == null || v2 == null || v1 === v2) return score;
      const bike1Wins = category.better === "lower" ? v1 < v2 : v1 > v2;
      return bike1Wins ? score + 1 : score - 1;
    }, 0);

  const winnerLabel =
    !compareReady || compareWinner === 0
      ? "Balanced matchup"
      : compareWinner > 0
      ? bikeOne.name
      : bikeTwo.name;

  const insightCards = compareReady
    ? [
        {
          label: "City friendly",
          winner: scoreCity(bikeOne) === scoreCity(bikeTwo) ? "Tie" : scoreCity(bikeOne) > scoreCity(bikeTwo) ? bikeOne.name : bikeTwo.name,
          note: "Mileage and kerb weight bias",
        },
        {
          label: "Best value",
          winner: scoreValue(bikeOne) === scoreValue(bikeTwo) ? "Tie" : scoreValue(bikeOne) > scoreValue(bikeTwo) ? bikeOne.name : bikeTwo.name,
          note: "Price and fuel-efficiency bias",
        },
        {
          label: "Highway pull",
          winner: scoreHighway(bikeOne) === scoreHighway(bikeTwo) ? "Tie" : scoreHighway(bikeOne) > scoreHighway(bikeTwo) ? bikeOne.name : bikeTwo.name,
          note: "Engine and stability bias",
        },
      ]
    : [];

  const selectBike = (bikeId, slot) => {
    if (slot === 1) {
      setBike1(resolveBikeId(bikeId));
      return;
    }
    setBike2(resolveBikeId(bikeId));
  };

  const preselectFromCard = (bikeId) => {
    const resolved = resolveBikeId(bikeId);
    if (!bike1 || bike1 === resolved) {
      setBike1(resolved);
      setToast("Bike 1 selected");
      return;
    }
    if (!bike2 || bike2 === resolved) {
      setBike2(resolved);
      setToast("Bike 2 selected");
      return;
    }
    setBike2(resolved);
    setToast("Bike 2 replaced");
  };

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setToast("Comparison link copied");
    } catch {
      setToast("Copy failed, please retry");
    }
  };

  const handleCompareNow = () => {
    if (!compareReady) return;
    setShowResult(true);
    navigate(`/compare/${bike1}-vs-${bike2}`);
    window.requestAnimationFrame(() => {
      resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(""), 1800);
    return () => clearTimeout(timer);
  }, [toast]);

  const renderComparisonValue = (category, bike) => {
    const value = category.getValue(bike);
    if (category.type === "text") return value;
    return category.format ? category.format(value) : String(value);
  };

  return (
    <div className="compare-page">
      <PageSeo
        title="Compare Bikes - MotoHub India"
        description="Compare trending bikes in a premium card layout with company filters, smart selectors, and side-by-side specs."
        keywords={[
          "bike comparison India",
          "compare bikes by company",
          "motorcycle comparison",
          "trending bikes India",
        ]}
        path="/compare"
      />

      <section className="compare-hero fade-up">
        <div className="compare-hero__copy">
          <div className="eyebrow">Compare smarter</div>
          <h1>Compare Bikes</h1>
          <p>
            Choose any two bikes from company filters or the trending card grid.
            The page now includes detailed spec ratios, quick ownership insights,
            and better route support for common bike matchups.
          </p>
          <div className="compare-stats">
            {statCards.map((item) => (
              <div key={item.label} className="compare-stat">
                <strong>{item.value}</strong>
                <span>{item.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="compare-hero__panel">
          <div className="compare-panel__title">Quick pick</div>
          <div className="compare-slot-grid">
            <label className="compare-slot">
              <span>Bike 1</span>
              <select value={bike1} onChange={(e) => selectBike(e.target.value, 1)}>
                <option value="">Select Bike 1</option>
                {bikes.map((bike) => (
                  <option key={bike.id} value={bike.id}>
                    {bike.brand} {bike.name}
                  </option>
                ))}
              </select>
            </label>

            <div className="compare-vs">VS</div>

            <label className="compare-slot">
              <span>Bike 2</span>
              <select value={bike2} onChange={(e) => selectBike(e.target.value, 2)}>
                <option value="">Select Bike 2</option>
                {bikes.map((bike) => (
                  <option key={bike.id} value={bike.id}>
                    {bike.brand} {bike.name}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="compare-actions">
            <button type="button" className="primary-btn" disabled={!compareReady} onClick={handleCompareNow}>
              Compare Now
            </button>
            <button type="button" className="ghost-btn" onClick={handleShare}>
              Share Link
            </button>
          </div>

          <div className="compare-mini-note">
            Tip: pick by brand first, then tap any card to auto-fill the next slot.
          </div>
        </div>
      </section>

      {bikeOne && bikeTwo && (
        <div className="compare-mini-preview">
          <div className="compare-mini-card">
            <h4>{bikeOne.name}</h4>
            <div className="compare-mini-stats">
              <span>Rs {formatNumber(bikeOne.price, 0)}</span>
              <span>{bikeOne.engine_cc} cc</span>
              <span>{bikeOne.mileage} km/l</span>
            </div>
          </div>

          <div className="compare-mini-vs">VS</div>

          <div className="compare-mini-card">
            <h4>{bikeTwo.name}</h4>
            <div className="compare-mini-stats">
              <span>Rs {formatNumber(bikeTwo.price, 0)}</span>
              <span>{bikeTwo.engine_cc} cc</span>
              <span>{bikeTwo.mileage} km/l</span>
            </div>
          </div>

          <button type="button" onClick={handleCompareNow} className="compare-mini-btn">
            Compare Now ->
          </button>
        </div>
      )}

      <div className="compare-breadcrumb fade-up-1">
        <Link to="/">Home</Link>
        <span>&gt;</span>
        <Link to="/compare">Compare</Link>
        <span>&gt;</span>
        <strong>{compareReady ? `${bikeOne.name} vs ${bikeTwo.name}` : "Select Bikes"}</strong>
      </div>

      <section className="compare-toolbar fade-up-1">
        <div className="brand-filters">
          {brandList.map((item) => (
            <button
              type="button"
              key={item}
              className={brand === item ? "chip chip--active" : "chip"}
              onClick={() => setBrand(item)}
            >
              {item}
            </button>
          ))}
        </div>

        <label className="searchbox">
          <span>Search bike, brand, type</span>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Try Apache, Hero, Adventure..."
          />
        </label>
      </section>

      <section className="compare-content">
        <div className="section-heading fade-up-2">
          <div>
            <h2>Popular bikes to compare</h2>
            <p>Tap a card to fill Bike 1, then Bike 2. The grid stays clean and fast.</p>
          </div>
          <div className="section-meta">{filteredBikes.length} bikes visible</div>
        </div>

        <div className="bike-grid fade-up-2">
          {filteredBikes.map((bike) => {
            const selected = bike.id === bike1 || bike.id === bike2;
            return (
              <button
                key={bike.id}
                type="button"
                className={selected ? "compare-bike-card compare-bike-card--selected" : "compare-bike-card"}
                onClick={() => preselectFromCard(bike.id)}
              >
                <div className="compare-bike-card__top">
                  <div>
                    <span className="compare-bike-brand">{bike.brand}</span>
                    <h3>{bike.name}</h3>
                  </div>
                  <span className="compare-bike-price">Rs {formatNumber(bike.price, 0)}</span>
                </div>

                <div className="compare-bike-card__meta">
                  <span>{bike.type}</span>
                  <span>{bike.engine_cc} cc</span>
                  <span>{bike.mileage} km/l</span>
                </div>

                <div className="compare-bike-card__footer">
                  <span>{bike.weight} kg</span>
                  <span className="compare-card-link">Select bike</span>
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {showResult && compareReady ? (
        <section className="compare-table fade-up-3" ref={resultRef}>
          <div className="section-heading">
            <div>
              <h2>Side-by-side comparison</h2>
              <p>
                Clear, no-clutter data for quicker decision making. Detailed ratios below help
                with ownership, not just headline specs.
              </p>
            </div>
            <div className="winner-pill">
              Best fit: <strong>{winnerLabel}</strong>
            </div>
          </div>

          <div className="compare-table__grid">
            <div className="spec-column">
              <div className="spec-column__head">{bikeOne.name}</div>
              {comparisonCategories.map((item) => (
                <div key={item.key} className="spec-row">
                  <span>{item.label}</span>
                  <strong>{renderComparisonValue(item, bikeOne)}</strong>
                </div>
              ))}
            </div>

            <div className="vs-column">VS</div>

            <div className="spec-column">
              <div className="spec-column__head">{bikeTwo.name}</div>
              {comparisonCategories.map((item) => (
                <div key={item.key} className="spec-row">
                  <span>{item.label}</span>
                  <strong>{renderComparisonValue(item, bikeTwo)}</strong>
                </div>
              ))}
            </div>
          </div>

          <div className="compare-insight-grid">
            {insightCards.map((item) => (
              <div key={item.label} className="compare-insight-card">
                <p className="compare-insight-label">{item.label}</p>
                <strong className="compare-insight-value">{item.winner}</strong>
                <span className="compare-insight-note">{item.note}</span>
              </div>
            ))}
          </div>

          <p className="compare-mini-note" style={{ marginTop: 16 }}>
            Note: the extra ratios are calculated from the published spec fields in this app, so
            they are best used as a quick decision guide rather than a substitute for a full test ride.
          </p>
        </section>
      ) : null}

      {toast ? <div className="toast-badge">{toast}</div> : null}
    </div>
  );
}
